import { useMemo } from 'react'

import { useMargin, usePageWidth } from '@bm-js/atlascloud-ui'
import { DynamicContentType, GridData } from '@bm-js/atlascloud-shared'
import { useEditorData } from '../../utils/hooks'
import { gridLayoutMap } from '../../utils/content.utils'
import GridContentSection from '../GridContentSection'

type Props = {
  gridContent: DynamicContentType<GridData>
}

const GridContent = ({ gridContent }: Props) => {
  const { workingConfig } = useEditorData()
  const gridMargin = useMargin(gridContent)
  const pageWidth = usePageWidth(workingConfig.pageMaxWidth || 'normal')

  const borderClasses = useMemo(() => {
    if (!gridContent.data.topLine && !gridContent.data.bottomLine) return ''
    if (gridContent.data.topLine && !gridContent.data.bottomLine)
      return 'border-t border-neutral-300'
    if (!gridContent.data.topLine && gridContent.data.bottomLine)
      return 'border-b border-neutral-300'
    if (gridContent.data.topLine && gridContent.data.bottomLine)
      return 'border-y border-neutral-300'
  }, [gridContent.data.topLine, gridContent.data.bottomLine])

  const gapClasses = useMemo(() => {
    return gridContent.data.gap || 'gap-4'
  }, [gridContent.data.gap])

  const sectionColors = useMemo(() => {
    switch (gridContent.data.backgroundColor) {
      case 'primary':
        return 'bg-customerPrimary-400 text-customerInverse py-8'
      case 'accent':
        return 'bg-customerAccent-400 text-customerAccentInverse py-8'
      default:
        return ''
    }
  }, [gridContent.data.backgroundColor])

  const gridClass = useMemo(() => {
    return gridLayoutMap[gridContent.data.gridLayout] || gridLayoutMap['1fr']
  }, [gridContent.data.gridLayout])

  return (
    <div className={`${gridMargin} ${sectionColors} @container/grid`}>
      <div
        className={`px-4 grid ${borderClasses} ${gridClass} ${pageWidth} ${gapClasses}`}
      >
        {gridContent.data.content.map((section, gridIndex) => (
          <GridContentSection
            key={`${gridContent.uuid}_${gridIndex}`}
            section={section}
            sectionIndex={gridIndex}
            gridContent={gridContent}
          />
        ))}
      </div>
    </div>
  )
}

export default GridContent
